import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { normalizeMultiFilterValue } from './filter-operators';
import { executeSearchRequest } from 'tio-data/requests/search';
import type { Store as StoreService } from '@warp-drive/core';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import type { FieldOption } from './opensearch-field-mapper';

const MAX_DISTINCT_VALUES = 50;
// Use camelCase agg name — the Rails API camelizes response keys
const AGG_NAME = 'distinctVals';

/** Convert camelCase field key to snake_case for the OpenSearch index */
function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/** Returns true for fields whose distinct values can be fetched (keyword/text types). */
export function shouldFetchDistinctValues(field: FieldOption): boolean {
  return field.type === 'keyword' || field.type === 'text';
}

/**
 * Fetches distinct values for a field via OpenSearch terms aggregation.
 * Returns sorted string array if ≤ 50 distinct values, otherwise null.
 *
 * @param activeFilters - Current filter values to apply as query context (for cascading).
 *   Keys should be camelCase field keys; the field being fetched is automatically excluded.
 */
export async function fetchDistinctValues(
  store: StoreService,
  fieldKey: string,
  companyId?: string,
  activeFilters?: Record<string, unknown>,
  modelType?: string,
  aggFieldPath?: string
): Promise<string[] | null> {
  const builder = new OpenSearchQueryBuilder().setPagination(0, 0);

  if (companyId) {
    builder.mustTerm('company_id', companyId);
  }

  // Apply active filter values as constraints (cascading filters)
  if (activeFilters) {
    for (const [key, value] of Object.entries(activeFilters)) {
      // Skip the field we're fetching distinct values for
      if (key === fieldKey || key.startsWith(`${fieldKey}__`)) continue;
      // Skip empty values
      if (value === '' || value === undefined || value === null) continue;
      // Skip empty arrays (e.g. freshly selected in/not_in with no selections yet)
      if (Array.isArray(value) && value.length === 0) continue;

      // Handle operator suffixes
      if (key.endsWith('__is_empty')) {
        const baseField = toSnakeCase(key.replace(/__is_empty$/, ''));
        builder.mustNotExist(baseField);
      } else if (key.endsWith('__is_not_empty')) {
        const baseField = toSnakeCase(key.replace(/__is_not_empty$/, ''));
        builder.exists(baseField);
      } else if (key.endsWith('__gte')) {
        const baseField = toSnakeCase(key.replace(/__gte$/, ''));
        builder.range(baseField, String(value), undefined);
      } else if (key.endsWith('__lte')) {
        const baseField = toSnakeCase(key.replace(/__lte$/, ''));
        builder.range(baseField, undefined, String(value));
      } else if (key.endsWith('__gt')) {
        const baseField = toSnakeCase(key.replace(/__gt$/, ''));
        builder.rangeOp(baseField, 'gt', String(value));
      } else if (key.endsWith('__lt')) {
        const baseField = toSnakeCase(key.replace(/__lt$/, ''));
        builder.rangeOp(baseField, 'lt', String(value));
      } else if (key.endsWith('__in')) {
        const baseField = toSnakeCase(key.replace(/__in$/, ''));
        const values = normalizeMultiFilterValue(value);
        builder.mustTerms(baseField, values);
      } else if (key.endsWith('__not_in')) {
        const baseField = toSnakeCase(key.replace(/__not_in$/, ''));
        const values = normalizeMultiFilterValue(value);
        builder.mustNotTerms(baseField, values as Array<string | number>);
      } else if (key.endsWith('__ne')) {
        const baseField = toSnakeCase(key.replace(/__ne$/, ''));
        builder.mustNotTerm(baseField, String(value));
      } else if (key.endsWith('__contains')) {
        const baseField = toSnakeCase(key.replace(/__contains$/, ''));
        builder.mustWildcard(baseField, `*${String(value)}*`);
      } else if (key.endsWith('__starts_with')) {
        const baseField = toSnakeCase(key.replace(/__starts_with$/, ''));
        builder.mustPrefix(baseField, String(value));
      } else {
        const snakeKey = toSnakeCase(key);
        builder.mustTerm(snakeKey, String(value));
      }
    }
  }

  // Field keys are camelCase (e.g. tasProgramTemplateCodeKeyword) but the
  // OpenSearch index uses snake_case (e.g. tas_program_template_code_keyword)
  const snakeField = aggFieldPath ?? toSnakeCase(fieldKey);
  builder.addTermsAggregation(AGG_NAME, snakeField, MAX_DISTINCT_VALUES + 1);

  // WarpDrive wave 1.5: execute via the shared search builder (replaces the
  // injected `searchAdapter.search`); unwrap `.content`.
  const { content: response } = await store.request<OpenSearchResponse>(
    executeSearchRequest({
      'model-type': modelType || 'TasProgramInstance',
      searchBody: builder.build(),
    })
  );

  // The Rails API camelizes response keys, so aggregations are at
  // response.aggregations. The terms aggregation is keyed by AGG_NAME, which is
  // outside the typed OpenSearchResponse.aggregations shape — narrow via a loose
  // cast (the injected adapter's untyped return previously allowed this).
  const aggs = response?.aggregations as unknown as
    | Record<string, { buckets?: Array<{ key: string; docCount?: number; doc_count?: number }> }>
    | undefined;

  const buckets = aggs?.[AGG_NAME]?.buckets || [];

  if (buckets.length > MAX_DISTINCT_VALUES) {
    return null;
  }

  return buckets
    .map((b) => String(b.key))
    .filter((v) => v !== '' && v !== 'null' && v !== 'undefined')
    .sort((a, b) => a.localeCompare(b));
}
